import React, { useCallback } from 'react'
import { useMutation, useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { createLoan } from '../../../services/library/loan';
import { LoanType } from '../../../types/Library';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { LoanFormValidation } from './LoanFormValidation';
import toast from 'react-hot-toast';
import { routes } from '../../../routes';
import { Button, Card, Flex, Heading, Spinner } from '@radix-ui/themes';
import { useGetBooksQuery } from '../../../hooks/library/useGetBooksQuery';
import SelectComponent from '../../../components/inputs/Select/SelectComponent';
import { UserType } from '../../../types/User';
import { key_userBi, key_userNuit } from '../../../utils/constants';
import { getUserByBI, getUserByNuit } from '../../../services/user/getUsers';
import TextFieldComponent from '../../../components/inputs/Textfield/TextfieldComponent';
import { debounce } from 'lodash';
import DateFieldComponent from '../../../components/inputs/Datefield/DatefieldComponent';
import RadioGroupComponent from '../../../components/inputs/RadioGroup/RadioGroupComponent';
import { useAppDispatch } from '../../../hooks/hooks';
import { resetUserData, setUserData } from '../../../redux/slices/users/user';
import { UserPreview } from '../../Users/UserPreview';
import { ChevronLeftIcon, EnterIcon } from '@radix-ui/react-icons';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import { resetPreviousPath } from '../../../redux/slices/previousPath';
import { resetBookData } from '../../../redux/slices/library/book';

export const CreateLoan = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const bookFormData = useSelector((state: RootState) => state.book);
    const previousPath = useSelector((state: RootState) => state.previousPath);

    const mutation = useMutation({ mutationFn: createLoan, mutationKey: "createLoan" });
    const defaultValues: LoanType = {
        idBook: bookFormData?.id??'',
        idUser: '',
        loanDate: new Date().toISOString(),
        dueDate: new Date().toISOString(),
    };

    type LoanFormValues = LoanType & {
        user: UserType;
        searchNuit?: string,
        searchBi?: string,
    };
    const { control, handleSubmit, formState: { errors }, reset, getValues, setValue } = useForm<LoanFormValues>({
        resolver: yupResolver(LoanFormValidation),
        defaultValues,
        mode: "onBlur",
    });
    const onSubmit = handleSubmit(async (data) => {
        try {
            await mutation.mutateAsync({...data});
            toast.success("Empréstimo criado!");
            navigate(routes.loans);
        } catch (error: any) {
            toast.error(`Erro: ${error.message}`);
        }
    });
    const handleBack = () =>{
        const path = previousPath?.previousPath;
        dispatch(resetUserData());
        dispatch(resetPreviousPath())
        dispatch(resetBookData())
        if(path){
            navigate(path)
            return;
        }
        navigate(routes.loans);
    } 
    const { data: booksData, status: statusBooks, isLoading: isLoadingBooks, isError: isErrorBooks, refetch: refreshBooks } = useGetBooksQuery({
        PageSize: 4000,
        PageNumber: 1,
    });

    const { isLoading: loadingUserByBi, refetch: fetchUserByBi, } = useQuery(
        [key_userBi],
        async () => {
            try {
                const userData = await getUserByBI(getValues('searchBi')!);
                if (!userData) return;
                reset({
                    searchBi: getValues('searchBi')!,
                    idUser: userData.id,
                    userFullname: userData.name,
                    status: true,
                    user: userData,
                })
                dispatch(setUserData(userData));
                return userData;
            } catch (error) {
                dispatch(resetUserData());
            }
        },
        {
            enabled: false,
            refetchOnWindowFocus: false,
            retry: false
        }
    );
    const { isLoading: loadingUserByNuit, refetch: fetchUserByNuit, } = useQuery(
        [key_userNuit],
        async () => {
            try {
                const userData = await getUserByNuit(getValues('searchNuit')!);
                if (!userData) return;
                reset({
                    searchBi: getValues('searchBi')!,
                    idUser: userData.id,
                    userFullname: userData.name,
                    status: true,
                    user: userData
                })
                dispatch(setUserData(userData));
                return userData;
            } catch (error) {
                dispatch(resetUserData());
            }
        },
        {
            enabled: false,
            refetchOnWindowFocus: false,
            retry: false
        }
    );


    const handleBIChange = useCallback(
        debounce((value: string) => {
            const trimmedValue = value.trim();
            setValue('searchBi', trimmedValue);
            if (trimmedValue.length >= 10) {
                fetchUserByBi();
            } else {
            }
        }, 300),
        [setValue
            , fetchUserByBi
        ]
    );

    const handleNUITChange = useCallback(
        debounce((value: string) => {
            const trimmedValue = value.trim();
            setValue('searchNuit', trimmedValue);
            if (trimmedValue.length >= 8) {
                fetchUserByNuit();
            } else {
            }
        }, 300),
        [setValue
            , fetchUserByNuit
        ]
    );

    const handleEmailChange = (e: any) => {
        const email = e.target.value;
        setValue("user.email", email);
        setValue("user.userName", email);
    }

    const handleFullnameChange = (e: any) => {
        const fullname = e.target.value;
        setValue("user.name", fullname);
        setValue("userFullname", fullname);
    }

    return (
        <form onSubmit={onSubmit} noValidate>
            <Card size="1">
                <Flex direction="column" gap="4">
                            <Flex gap='2'>
                                <TextFieldComponent
                                    style={{ width: '100%' }}
                                    name="searchBi"
                                    label="Procurar por BI"
                                    control={control}
                                    errors={errors}
                                    fieldProps={{
                                        placeholder: 'Procurar por BI',
                                        onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleBIChange(e.target.value),
                                        onPaste: (e: React.ClipboardEvent<HTMLInputElement>) => {
                                            e.preventDefault();
                                            const pastedText = e.clipboardData.getData('text');
                                            handleBIChange(pastedText);
                                        }
                                    }}
                                />
                                <TextFieldComponent
                                    style={{ width: '100%' }}
                                    name="searchNuit"
                                    label="Procurar por NUIT"
                                    control={control}
                                    errors={errors}
                                    fieldProps={{
                                        placeholder: 'Procurar por NUIT',
                                        onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleNUITChange(e.target.value),
                                        onPaste: (e: React.ClipboardEvent<HTMLInputElement>) => {
                                            e.preventDefault();
                                            const pastedText = e.clipboardData.getData('text');
                                            handleNUITChange(pastedText);
                                        }
                                    }}
                                />
                            </Flex>
                            {loadingUserByNuit || loadingUserByBi ? <Spinner loading></Spinner>
                                : <UserPreview />
                            }
                        <SelectComponent
                            label="Livro"
                            options={booksData?.items.map((book) => ({
                                value: book?.id ?? "",
                                label: `${book?.title}`
                            }))}
                            errors={errors}
                            control={control}
                            name="idBook"
                            itemsLoading={isLoadingBooks}
                            itemsError={isErrorBooks}
                            style={{ width: '100%' }}
                            required={true}
                        />
                    <Flex direction='row' gap='4'>
                        <DateFieldComponent name="loanDate" label="Data de empréstimo" control={control} errors={errors} style={{ width: '100%' }} />
                        <DateFieldComponent name="dueDate" label="Data de entrega prevista" control={control} errors={errors} style={{ width: '100%' }} />
                    </Flex>
                    {/* <Heading size='2' title='Registar ou buscar utilizador'>
                    Buscar ou pesquisar utilizador
                    </Heading> */}

                    <Flex direction="row" gap="4" style={{ alignSelf: "end" }}>
                        <Button color="red" onClick={handleBack} disabled={mutation.isLoading}>
                            <ChevronLeftIcon /> Voltar
                        </Button>
                        <Button type="submit" disabled={mutation.isLoading} variant="surface">
                            {mutation.isLoading ? (
                                <Spinner loading>
                                    <EnterIcon />
                                </Spinner>
                            ) : (
                                <EnterIcon />
                            )}
                            Registar
                        </Button>
                    </Flex>
                    {/* <Flex gap="8">
                        <TextFieldComponent name="user.name" composedName={{ parent: "user", prop: 'name' }} label="Nome" control={control} errors={errors} fieldProps={{ placeholder: 'Nome completo', onChange: handleFullnameChange, type: 'text' }} style={{ width: '100%' }} />
                        <TextFieldComponent name="user.userName" composedName={{ parent: "user", prop: 'userName' }} label="Utilizador" control={control} errors={errors} fieldProps={{ placeholder: 'Utilizador', disabled: 'disabled' }} style={{ width: '100%' }} />
                        <TextFieldComponent name="user.email" composedName={{ parent: "user", prop: 'email' }} label="Email" control={control} errors={errors} fieldProps={{ placeholder: 'seu@email.com', onChange: handleEmailChange, type: 'email' }} style={{ width: '100%' }} />
                    </Flex>
                    <Flex gap="8">
                        <TextFieldComponent name="user.bi" composedName={{ parent: "user", prop: 'bi' }} label="BI" control={control} errors={errors} fieldProps={{ placeholder: 'BI' }} style={{ width: '100%' }} />
                        <TextFieldComponent name="user.nuit" composedName={{ parent: "user", prop: 'nuit' }} label="NUIT" control={control} errors={errors} fieldProps={{ placeholder: 'Seu NUIT' }} style={{ width: '100%' }} />
                        <TextFieldComponent name="user.phoneNumber" composedName={{ parent: "user", prop: 'phoneNumber' }} label="Telefone" control={control} errors={errors} fieldProps={{ placeholder: '123456789' }} style={{ width: '100%' }} />
                    </Flex>
                    <Flex gap="8">
                        <DateFieldComponent name="user.dateOfBirth" composedName={{ parent: "user", prop: 'dateOfBirth' }} label="Data de Nascimento" control={control} errors={errors} style={{ width: '32%' }} />
                        <RadioGroupComponent name="user.gender" composedName={{ parent: "user", prop: 'gender' }} label="Gênero" control={control} errors={errors} style={{ width: '68%' }}
                            options={[
                                { value: "M", label: "Masculino" }
                                , { value: "F", label: "Feminino" }]}
                            fieldProps={{
                                style: { display: 'flex', flexDirection: 'row', gap: '16px' }
                            }}
                        />
                    </Flex> */}

                </Flex>
            </Card>
        </form>
    )
}
